/**
 * @component ry-color-input
 * @category  form
 * @summary   Input-style color picker (ry-ui-only). A swatch button + editable hex input that opens a `<ry-color-picker>` panel on click. The picker is composed internally — set the hex via the input, click the swatch to open the visual picker, click outside to close.
 *
 * @attribute value        string?   default="#000000"   Initial color value. Accepts hex (`#ff0000`), short hex (`#f00`), rgb(), hsl(), or any CSS color string supported by the inner picker's `setColor`.
 * @attribute format       'hex' | 'rgb' | 'hsl' ?   default="hex"   Format the picker uses for its `.value` getter. The text input always shows hex.
 * @attribute opacity      boolean   default=false   Enable the alpha channel in the inner picker.
 * @attribute placeholder  string?              Placeholder hex for the empty input. Auto-prefixed with `#` if missing.
 * @attribute disabled     boolean   default=false   Non-interactive — input + swatch button + picker all suppressed.
 *
 * @property  value     string   Read/write — current color in the configured format.
 * @property  rgb       RGB      Read-only — `{r, g, b}` 0-255.
 * @property  hsl       HSL      Read-only — `{h, s, l}` h 0-360, s/l 0-100.
 * @property  hsv       HSV      Read-only — `{h, s, v}` h 0-360, s/v 0-100.
 * @property  disabled  boolean  Live disabled state.
 *
 * @event ry:input
 *        detail: { value: string }
 *        fires:  on every value change — input keystroke that produces a valid color, picker drag, picker hex edit. Use for live preview.
 *
 * @event ry:beforechange
 *        detail: { value: string, previous: string }
 *        fires:  before each commit — Enter in the hex input, or a commit from the inner picker (drag release, arrow keys, swatch, hex) — with `value` the color ABOUT to be committed and `previous` the last committed value. Not fired for the `ry:input` stream.
 *        cancelable: preventDefault() reverts input, swatch, and picker to `previous`; `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value: string }
 *        fires:  on commit — input blur with a valid value, picker drag-end, picker hex commit. Same shape as `ry:input` but throttled to commit points.
 *
 * @state     `data-ry-state` of `"open"` / `"closed"` mirrors the picker panel's open state.
 *
 * @content   None — `<ry-color-input>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-color-input[value][format?][opacity?][disabled?]
 *     > div.ry-color-input__trigger    [data-ry-target="trigger"]
 *       > button.ry-color-input__swatch    [data-ry-target="swatch"]   type="button"
 *         > span.ry-color-input__swatch-color   [data-ry-target="swatch-color"]   style="background:value"
 *       > input.ry-color-input__input    [data-ry-target="input"]   type="text"   maxlength="7"   spellcheck="false"
 *     > div.ry-color-input__panel    [data-ry-target="panel"]   (visible only when state="open")
 *       > ry-color-picker    [data-ry-target="picker"]   inline   value=...
 *
 * @gotcha  Text input is hex-ONLY. The configured `[format]` only affects `.value` getter output and the inner picker's display — typing `rgb(...)` or `hsl(...)` in the input does NOT work. To set non-hex programmatically, use `el.value = "rgb(255, 0, 0)"`.
 *
 * @gotcha  Setting `el.value = "not-a-color"` fails silently. The inner picker's `setColor` returns `false`, the setter early-returns, swatch + input keep their previous state. No warning. Verify color before setting.
 *
 * @gotcha  `<ry-color-picker inline>` is what's composed — the inline variant skips the picker's own popover/anchor logic since we wrap it in our own panel. Don't move the inner picker out of the panel.
 *
 * @gotcha  `ry:input` fires for every keystroke that PRODUCES a valid color (a partial like "#ff" is silent). `ry:change` fires on commit only. For real-time preview as the user types each digit, listen to `ry:input` and accept that fewer events fire than keystrokes.
 *
 * @example basic
 * ```html
 * <ry-color-input value="#ff0000"></ry-color-input>
 * ```
 *
 * @example with-opacity
 * ```html
 * <ry-color-input value="#ff0000" opacity></ry-color-input>
 * ```
 *
 * @example rgb-format
 * ```html
 * <ry-color-input value="#ff0000" format="rgb"></ry-color-input>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-color-input value="#ff0000" disabled></ry-color-input>
 * ```
 *
 * @tutorial mount-builds-trigger-and-panel
 * ```js
 * const el = mount('<ry-color-input value="#ff0000"></ry-color-input>');
 * await expectEqual(!!el.querySelector('[data-ry-target="trigger"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="swatch"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="input"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="panel"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="picker"]'), true);
 * ```
 *
 * @tutorial starts-closed
 * ```js
 * const el = mount('<ry-color-input value="#ff0000"></ry-color-input>');
 * await expectAttr(el, 'data-ry-state', 'closed');
 * ```
 *
 * @tutorial input-shows-hex-value
 * ```js
 * const el = mount('<ry-color-input value="#ff0000"></ry-color-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * await expectEqual(input.value, '#ff0000');
 * ```
 *
 * @tutorial value-setter-updates-input-and-swatch
 * ```js
 * const el = mount('<ry-color-input value="#000000"></ry-color-input>');
 * el.value = '#ff00ff';
 * const input = el.querySelector('[data-ry-target="input"]');
 * await expectEqual(input.value.toLowerCase(), '#ff00ff', 'input synced');
 * ```
 *
 * @tutorial value-setter-rejects-invalid-silently
 * ```js
 * // Wrong assumption: invalid colors throw or clear. Setter no-ops.
 * const el = mount('<ry-color-input value="#ff0000"></ry-color-input>');
 * el.value = 'this-is-not-a-color';
 * await expectEqual(el.value.toLowerCase(), '#ff0000', 'previous value survives');
 * ```
 *
 * @tutorial rgb-hsl-hsv-getters
 * ```js
 * const el = mount('<ry-color-input value="#ff0000"></ry-color-input>');
 * await expectEqual(el.rgb.r, 255);
 * await expectEqual(el.rgb.g, 0);
 * await expectEqual(el.rgb.b, 0);
 * await expectEqual(typeof el.hsl.h, 'number');
 * await expectEqual(typeof el.hsv.v, 'number');
 * ```
 *
 * @tutorial disabled-attribute-propagates-to-input
 * ```js
 * const el = mount('<ry-color-input value="#ff0000" disabled></ry-color-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * await expectEqual(input.hasAttribute('disabled'), true);
 * ```
 *
 * @tutorial disabled-setter-toggles-attribute
 * ```js
 * const el = mount('<ry-color-input value="#ff0000"></ry-color-input>');
 * el.disabled = true;
 * await expectEqual(el.hasAttribute('disabled'), true);
 * el.disabled = false;
 * await expectEqual(el.hasAttribute('disabled'), false);
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange reverts to the last committed color
 * // and ry:change stays silent — e.g. "only brand colors allowed".
 * const el = mount('<ry-color-input value="#ff0000"></ry-color-input>');
 * const input = el.querySelector('[data-ry-target="input"]');
 * const changed = [];
 * el.addEventListener('ry:change', (e) => { if (e.target === el) changed.push(e.detail.value); });
 * el.addEventListener('ry:beforechange', (e) => {
 *   if (e.target === el && e.detail.value === '#00ff00' && e.detail.previous === '#ff0000') e.preventDefault();
 * });
 * input.value = '#00ff00';
 * input.dispatchEvent(new Event('input', { bubbles: true }));   // live preview (ry:input)
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.value, '#ff0000', 'vetoed commit reverts to the committed color');
 * await expectEqual(input.value, '#ff0000', 'input reverts too');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * input.value = '#0000ff';
 * input.dispatchEvent(new Event('input', { bubbles: true }));
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.value, '#0000ff', 'a color the guard allows commits');
 * await expectEqual(changed[0], '#0000ff', 'ry:change fired with the new color');
 * ```
 *
 * @see ry-color-picker
 * @see ry-gradient-picker
 */

import { RyElement } from '../core/ry-element.js';
import type { RyColorPicker, RGB, HSL, HSV } from './ry-color-picker.js';

export class RyColorInput extends RyElement {
  #swatch: HTMLElement | null = null;
  #input: HTMLInputElement | null = null;
  #panel: HTMLElement | null = null;
  #picker: RyColorPicker | null = null;
  /** Last committed value — `previous` in ry:beforechange; a veto reverts to it. */
  #committed = '';

  static observedAttributes = ['value', 'format', 'opacity', 'disabled', 'placeholder'] as const;

  setup(): void {
    this.#buildMarkup();
    this.#bindEvents();
    this.#syncFromValue();
    this.#committed = this.value;
    // Start closed — write directly because RyElement's state setter no-ops
    // when newState === default ('closed'), so the attribute would never set.
    this.dataset.ryState = 'closed';
    // Propagate initial [disabled] to the inner input (the
    // attributeChangedCallback ignores pre-init changes by design).
    if (this.hasAttribute('disabled') && this.#input) {
      this.#input.setAttribute('disabled', '');
    }
  }

  #buildMarkup(): void {
    const showAlpha = this.hasAttribute('opacity');
    const format = this.getAttribute('format') || 'hex';
    const value = this.getAttribute('value') || '#000000';
    const placeholder = this.getAttribute('placeholder') || '#000000';

    this.innerHTML = `
      <div data-ry-target="trigger" class="ry-color-input__trigger">
        <button data-ry-target="swatch" type="button" class="ry-color-input__swatch">
          <span data-ry-target="swatch-color" class="ry-color-input__swatch-color"></span>
        </button>
        <input
          data-ry-target="input"
          type="text"
          class="ry-color-input__input"
          value="${value.startsWith('#') ? value : '#' + value}"
          placeholder="${placeholder.startsWith('#') ? placeholder : '#' + placeholder}"
          spellcheck="false"
          autocomplete="off"
          maxlength="7"
        />
      </div>
      <div data-ry-target="panel" class="ry-color-input__panel">
        <ry-color-picker
          data-ry-target="picker"
          inline
          value="${value}"
          format="${format}"
          ${showAlpha ? 'opacity' : ''}
        ></ry-color-picker>
      </div>
    `;

    this.#swatch = this.$('[data-ry-target="swatch"]');
    this.#input = this.$('[data-ry-target="input"]');
    this.#panel = this.$('[data-ry-target="panel"]');
    this.#picker = this.$('[data-ry-target="picker"]') as RyColorPicker;
  }

  #bindEvents(): void {
    // Swatch click opens/closes panel
    if (this.#swatch) {
      this.on(this.#swatch, 'click', this.#handleSwatchClick);
    }

    // Input changes update the picker
    if (this.#input) {
      this.on(this.#input, 'input', this.#handleInputChange);
      this.on(this.#input, 'keydown', this.#handleInputKeydown);
      this.on(this.#input, 'focus', this.#handleInputFocus);
    }

    // Picker changes update the input
    if (this.#picker) {
      this.on(this.#picker, 'ry:input', this.#handlePickerInput);
      this.on(this.#picker, 'ry:change', this.#handlePickerChange);
    }

    // Click outside to close
    this.on(document, 'click', this.#handleDocumentClick);

    // Escape to close
    this.on(document, 'keydown', this.#handleDocumentKeydown);
  }

  #handleSwatchClick = (): void => {
    if (this.disabled) return;
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  };

  #handleInputFocus = (): void => {
    if (this.disabled) return;
    this.open();
  };

  #handleInputChange = (): void => {
    if (!this.#input || !this.#picker) return;

    let value = this.#input.value;
    const cursorPos = this.#input.selectionStart ?? value.length;

    // Filter to only # and hex characters
    const filtered = value.replace(/[^#a-fA-F0-9]/g, '');

    // Normalize: ensure single # at start
    const hexPart = filtered.replace(/#/g, '').slice(0, 6);
    const normalized = '#' + hexPart;

    // Only update input if we actually changed something
    if (value !== normalized) {
      this.#input.value = normalized;
      // Keep cursor in reasonable position
      const newPos = Math.min(cursorPos, normalized.length);
      this.#input.setSelectionRange(newPos, newPos);
    }

    // Update picker with expanded color
    const expanded = this.#expandPartialHex(normalized);
    if (expanded) {
      this.#picker.setColor(expanded);
      this.#updateSwatch();
      if (this.#isCompleteColor(normalized)) {
        this.#syncValueAttribute();
      }
      this.emit('input', { value: this.value });
    }
  };

  #expandPartialHex(value: string): string | null {
    let color = value;
    if (!color.startsWith('#')) color = '#' + color;

    const hex = color.slice(1);
    if (!/^[a-fA-F0-9]+$/.test(hex) || hex.length === 0) {
      return null;
    }

    // 6 chars - use as-is
    if (hex.length === 6) {
      return color;
    }

    // 1-3 chars - pad to 3, then CSS shorthand expand
    // #f → #fff → #ffffff
    // #fa → #faa → #ffaaaa
    // #f00 → #ff0000
    if (hex.length <= 3) {
      const padded = hex.padEnd(3, hex[hex.length - 1]!);
      const [r, g, b] = padded.split('');
      return `#${r}${r}${g}${g}${b}${b}`;
    }

    // 4-5 chars - pad with zeros
    // #f00a → #f00a00
    // #f00ab → #f00ab0
    return '#' + hex.padEnd(6, '0');
  }

  #isCompleteColor(value: string): boolean {
    // Only 3 or 6 char hex are "complete"
    if (/^#[a-fA-F0-9]{6}$/.test(value)) return true;
    if (/^#[a-fA-F0-9]{3}$/.test(value)) return true;
    return false;
  }

  #handleInputKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.#handleInputChange();
      this.#commit();
    }
  };

  /**
   * Commit choke point. The value is already live (ry:input), so the
   * before-event judges it against the last committed value; a veto reverts
   * input, swatch, and picker to that value.
   */
  #commit(): void {
    const next = this.value;
    const previous = this.#committed;
    if (!this.emit('beforechange', { value: next, previous })) {
      this.value = previous;
      return;
    }
    this.#committed = next;
    this.emit('change', { value: next });
  }

  #handlePickerInput = (): void => {
    if (!this.#picker || !this.#input) return;

    // Don't overwrite input if user is typing in it
    if (document.activeElement !== this.#input) {
      this.#input.value = this.#picker.value;
    }

    this.#updateSwatch();
    this.#syncValueAttribute();
    this.emit('input', { value: this.value });
  };

  #handlePickerChange = (): void => {
    this.#commit();
  };

  #handleDocumentClick = (e: Event): void => {
    if (this.state !== 'open') return;
    if (!this.contains(e.target as Node)) {
      this.close();
    }
  };

  #handleDocumentKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.state === 'open') {
      this.close();
      this.#swatch?.focus();
    }
  };

  open(): void {
    this.#positionPanel();
    this.state = 'open';
  }

  close(): void {
    this.state = 'closed';
    this.removeAttribute('data-ry-position');
  }

  #positionPanel(): void {
    if (!this.#panel) return;

    const rect = this.getBoundingClientRect();
    const panelHeight = 280;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < panelHeight && spaceAbove > spaceBelow) {
      this.setAttribute('data-ry-position', 'top');
    } else {
      this.setAttribute('data-ry-position', 'bottom');
    }
  }

  #updateSwatch(): void {
    const swatchColor = this.$('[data-ry-target="swatch-color"]');
    if (swatchColor && this.#picker) {
      const rgb = this.#picker.rgb;
      const alpha = this.hasAttribute('opacity') ? this.#picker.alpha / 100 : 1;
      (swatchColor as HTMLElement).style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }
  }

  #syncFromValue(): void {
    const value = this.getAttribute('value') || '#000000';
    if (this.#picker) {
      this.#picker.setColor(value);
      if (this.#input) {
        // Always show as hex with #
        const hex = this.#picker.value;
        this.#input.value = hex.startsWith('#') ? hex : '#' + hex;
      }
      this.#updateSwatch();
    }
  }

  #syncValueAttribute(): void {
    if (this.#picker) {
      const formatted = this.#picker.value;
      if (this.getAttribute('value') !== formatted) {
        this.setAttribute('value', formatted);
      }
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    if (name === 'value' && this.#picker && this.#input) {
      if (newValue) {
        this.#picker.setColor(newValue);
        // Don't overwrite input if user is typing
        if (document.activeElement !== this.#input) {
          this.#input.value = this.#picker.value;
        }
        this.#updateSwatch();
      }
    }

    if (name === 'disabled') {
      if (this.#input) {
        if (newValue !== null) {
          this.#input.setAttribute('disabled', '');
        } else {
          this.#input.removeAttribute('disabled');
        }
      }
    }
  }

  // Public API
  get value(): string {
    return this.#picker?.value ?? this.getAttribute('value') ?? '#000000';
  }

  set value(val: string) {
    if (this.#picker?.setColor(val)) {
      if (this.#input) {
        this.#input.value = this.#picker.value;
      }
      this.#updateSwatch();
      this.#syncValueAttribute();
      this.#committed = this.#picker.value;
    }
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  get rgb(): RGB {
    return this.#picker?.rgb ?? { r: 0, g: 0, b: 0 };
  }

  get hsl(): HSL {
    return this.#picker?.hsl ?? { h: 0, s: 0, l: 0 };
  }

  get hsv(): HSV {
    return this.#picker?.hsv ?? { h: 0, s: 0, v: 0 };
  }
}

customElements.define('ry-color-input', RyColorInput);
